import { asset } from '$app/paths';

import { gifProviderIds, type GifProviderId, type GifsConfig } from '#lib/gif/providers.js';

/** Key names follow v1's `config.json` so a deployment can carry its file over. */
export type PushDetails = {
  pushNotifyUrl: string;
  vapidPublicKey: string;
  webPushAppID: string;
  nativePushAppID: string | null;
  iosPushAppID?: string | null;
  unifiedPushEmbeddedServerUrl?: string | null;
  unifiedPushGatewayUrl?: string | null;
};

export type HomeserversConfig = {
  list: string[];
  default: string;
  allowCustom: boolean;
};

export type CallsConfig = {
  livekitServiceUrl: string | null;
};

export type SupporterConfig = {
  serviceUrl: string;
  keys: Readonly<Record<string, string>>;
};

export type EmbedsConfig = {
  serviceUrl: string | null;
};

export type RuntimeConfig = {
  push: PushDetails | null;
  embeds: EmbedsConfig;
  supporter: SupporterConfig | null;
  gifs: GifsConfig;
  homeservers: HomeserversConfig;
  exploreServers: string[];
  calls: CallsConfig;
  disableAccountSwitcher: boolean;
  settingsDefaults: Record<string, unknown>;
  hideUsernamePasswordFields: boolean;
};

const NO_GIFS: GifsConfig = {
  provider: null,
  proxyUrl: null,
  klipyApiKey: null,
  tenorApiKey: null,
  giphyApiKey: null,
};

export const BUILT_IN_HOMESERVERS: HomeserversConfig = {
  list: ['matrix.org', 'mozilla.org', 'unredacted.org', 'sable.moe', 'kendama.moe', 'hopium.club'],
  default: 'matrix.org',
  allowCustom: true,
};

const NO_CALLS: CallsConfig = { livekitServiceUrl: null };

const NO_EMBEDS: EmbedsConfig = { serviceUrl: null };

const EMPTY: RuntimeConfig = {
  push: null,
  embeds: NO_EMBEDS,
  supporter: null,
  gifs: NO_GIFS,
  homeservers: BUILT_IN_HOMESERVERS,
  exploreServers: [],
  calls: NO_CALLS,
  disableAccountSwitcher: false,
  settingsDefaults: {},
  hideUsernamePasswordFields: false,
};

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** The three values a subscription needs are read as one unit; see
    `hasCompleteOverride` for why a partial set is worse than none. */
function parsePush(raw: unknown): PushDetails | null {
  if (typeof raw !== 'object' || raw === null) return null;

  const source = raw as Record<string, unknown>;
  const pushNotifyUrl = text(source.pushNotifyUrl);
  const vapidPublicKey = text(source.vapidPublicKey);
  const webPushAppID = text(source.webPushAppID);
  if (!pushNotifyUrl || !vapidPublicKey || !webPushAppID) return null;

  return {
    pushNotifyUrl,
    vapidPublicKey,
    webPushAppID,
    nativePushAppID: text(source.nativePushAppID),
    ...(text(source.iosPushAppID) ? { iosPushAppID: text(source.iosPushAppID) } : {}),
    ...(text(source.unifiedPushEmbeddedServerUrl)
      ? { unifiedPushEmbeddedServerUrl: text(source.unifiedPushEmbeddedServerUrl) }
      : {}),
    ...(text(source.unifiedPushGatewayUrl)
      ? { unifiedPushGatewayUrl: text(source.unifiedPushGatewayUrl) }
      : {}),
  };
}

const ED25519_PUBLIC_KEY = /^[A-Za-z0-9+/_-]{43}$/;

function parseSupporter(raw: unknown): SupporterConfig | null {
  if (typeof raw !== 'object' || raw === null) return null;

  const source = raw as Record<string, unknown>;
  const serviceUrl = text(source.serviceUrl)?.replace(/\/+$/, '');
  if (!serviceUrl || !/^https?:\/\//.test(serviceUrl)) return null;

  const keys =
    typeof source.keys === 'object' && source.keys !== null && !Array.isArray(source.keys)
      ? Object.entries(source.keys).flatMap(([id, key]) => {
          const value = text(key);
          return value && ED25519_PUBLIC_KEY.test(value) ? [[id, value] as const] : [];
        })
      : [];
  if (keys.length === 0) return null;

  return { serviceUrl, keys: Object.fromEntries(keys) };
}

function parseGifs(raw: unknown): GifsConfig {
  if (typeof raw !== 'object' || raw === null) return NO_GIFS;

  const source = raw as Record<string, unknown>;
  const named = text(source.provider);

  return {
    provider: gifProviderIds.includes(named as GifProviderId) ? (named as GifProviderId) : null,
    proxyUrl: text(source.proxyUrl),
    klipyApiKey: text(source.klipyApiKey),
    tenorApiKey: text(source.tenorApiKey),
    giphyApiKey: text(source.giphyApiKey),
  };
}

function parseEmbeds(raw: unknown): EmbedsConfig {
  if (typeof raw !== 'object' || raw === null) return NO_EMBEDS;

  const serviceUrl = text((raw as Record<string, unknown>).serviceUrl)?.replace(/\/+$/, '');
  return { serviceUrl: serviceUrl && /^https?:\/\//.test(serviceUrl) ? serviceUrl : null };
}

function parseCalls(raw: unknown): CallsConfig {
  if (typeof raw !== 'object' || raw === null) return NO_CALLS;

  return { livekitServiceUrl: text((raw as Record<string, unknown>).livekitServiceUrl) };
}

function parseHomeservers(raw: unknown, allowCustom: unknown): HomeserversConfig {
  const list = Array.isArray(raw)
    ? [...new Set(raw.map(text).filter((server): server is string => server !== null))]
    : [];
  const custom = typeof allowCustom === 'boolean' ? allowCustom : BUILT_IN_HOMESERVERS.allowCustom;

  if (list.length === 0) return { ...BUILT_IN_HOMESERVERS, allowCustom: custom };
  return { list, default: list[0], allowCustom: custom };
}

function parseExploreServers(raw: unknown): string[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const servers = (raw as Record<string, unknown>).servers;
  return Array.isArray(servers)
    ? [...new Set(servers.map(text).filter((server): server is string => server !== null))]
    : [];
}

function withDefaultAt(homeservers: HomeserversConfig, index: unknown): HomeserversConfig {
  if (index === null) return { ...homeservers, default: '' };
  if (typeof index !== 'number' || !Number.isInteger(index)) return homeservers;
  if (index < 0 || index >= homeservers.list.length) return homeservers;

  return { ...homeservers, default: homeservers.list[index] };
}

export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  if (typeof raw !== 'object' || raw === null) return EMPTY;

  const source = raw as Record<string, unknown>;
  return {
    push: parsePush(source.pushNotificationDetails),
    embeds: parseEmbeds(source.embeds),
    supporter: parseSupporter(source.supporterAwards),
    gifs: parseGifs(source.gifs),
    homeservers: withDefaultAt(
      parseHomeservers(source.homeserverList, source.allowCustomHomeservers),
      source.defaultHomeserver
    ),
    exploreServers: parseExploreServers(source.featuredCommunities),
    calls: parseCalls(source.calls),
    disableAccountSwitcher: source.disableAccountSwitcher === true,
    settingsDefaults:
      typeof source.settingsDefaults === 'object' &&
      source.settingsDefaults !== null &&
      !Array.isArray(source.settingsDefaults)
        ? (source.settingsDefaults as Record<string, unknown>)
        : {},
    hideUsernamePasswordFields: source.hideUsernamePasswordFields === true,
  };
}

let loading: Promise<RuntimeConfig> | null = null;

/** Read once per session. A network failure is left uncached so that being
    offline at boot does not disable push until the tab is reloaded. */
export function runtimeConfig(): Promise<RuntimeConfig> {
  loading ??= fetch(asset('config.json'), { cache: 'no-cache' })
    .then((response) => (response.ok ? (response.json() as Promise<unknown>) : null))
    .then(parseRuntimeConfig)
    .catch((error: unknown) => {
      console.debug('[sable config] runtime config unavailable', error);
      loading = null;
      return EMPTY;
    });

  return loading;
}
